import React from "react";
import styles from "./CustomTable.module.css";

const CustomTable = ({
  columns = [],
  rows = [],
  actionButtons = [],
  onRowClick,
  emptyMessage = "No data available",
  loading = false,
  loadingMessage = "Loading...",
  className = "",
  striped = false,
  hoverable = true,
  bordered = false,
  compact = false,
  stickyHeader = false,
  rowKeyField = "id",
  renderCell,
  ...rest
}) => {
  const handleRowClick = (row, index) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  const renderCellContent = (row, column, rowIndex) => {
    // Custom cell renderer
    if (renderCell) {
      const customContent = renderCell(row, column, rowIndex);
      if (customContent !== undefined) {
        return customContent;
      }
    }

    // Column-specific renderer
    if (column.render) {
      return column.render(row[column.key], row, rowIndex);
    }

    // Default: return cell value
    return row[column.key] ?? "-";
  };

  const renderActionButtons = (row, rowIndex) => {
    if (!actionButtons || actionButtons.length === 0) return null;

    // Filter actions based on condition
    const visibleActions = actionButtons.filter((action) => {
      if (action.condition) {
        return action.condition(row, rowIndex);
      }
      return true;
    });

    if (visibleActions.length === 0) return null;

    return (
      <div className={styles.actionContainer}>
        {visibleActions.map((action, actionIndex) => {
          // Get dynamic style for button background - check if it's a function or object
          const dynamicStyle =
            typeof action.style === "function"
              ? action.style(row, rowIndex)
              : action.style || {};

          // Check if disabled is a function or boolean
          const isDisabled =
            typeof action.disabled === "function"
              ? action.disabled(row, rowIndex)
              : action.disabled || false;

          // Check if loading is a function or boolean
          const isLoading =
            typeof action.loading === "function"
              ? action.loading(row, rowIndex)
              : action.loading || false;

          return (
            <button
              key={action.id || actionIndex}
              type="button"
              className={`${styles.actionButton} ${
                styles[action.variant] || ""
              }`}
              style={dynamicStyle}
              onClick={(e) => {
                e.stopPropagation();
                if (action.onClick && !isDisabled && !isLoading) {
                  action.onClick(row, rowIndex);
                }
              }}
              title={action.tooltip || action.label}
              disabled={isDisabled || isLoading}
              aria-label={action.label}
            >
              {isLoading ? (
                <i
                  className="fa-solid fa-spinner fa-spin"
                  style={{ color: "#ffffff" }}
                />
              ) : (
                action.icon
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const tableClasses = [
    styles.table,
    striped ? styles.striped : "",
    hoverable ? styles.hoverable : "",
    bordered ? styles.bordered : "",
    compact ? styles.compact : "",
    stickyHeader ? styles.stickyHeader : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const hasActions = actionButtons && actionButtons.length > 0;

  return (
    <div className={styles.tableWrapper} {...rest}>
      <div className={styles.tableContainer}>
        <table className={tableClasses}>
          <thead className={styles.thead}>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key || index}
                  className={`${styles.th} ${column.headerClassName || ""}`}
                  style={{
                    width: column.width,
                    minWidth: column.minWidth,
                    textAlign: column.align || "left",
                  }}
                >
                  {column.label || column.header}
                </th>
              ))}
              {hasActions && (
                <th className={`${styles.th} ${styles.actionHeader}`}>
                  OPTION
                </th>
              )}
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  className={styles.loadingCell}
                >
                  <div className={styles.loadingContainer}>
                    <span className={styles.spinner}></span>
                    <span>{loadingMessage}</span>
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  className={styles.emptyCell}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => (
                <tr
                  key={row[rowKeyField] || rowIndex}
                  className={`${styles.tr} ${
                    onRowClick ? styles.clickable : ""
                  }`}
                  onClick={() => handleRowClick(row, rowIndex)}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={column.key || colIndex}
                      className={`${styles.td} ${column.cellClassName || ""}`}
                      style={{ textAlign: column.align || "left" }}
                    >
                      {renderCellContent(row, column, rowIndex)}
                    </td>
                  ))}
                  {hasActions && (
                    <td className={`${styles.td} ${styles.actionCell}`}>
                      {renderActionButtons(row, rowIndex)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomTable;
